/**
 * Flameco TIA app-shell detection.
 *
 * The Android app (Capacitor WebView) loads this site at
 * /threaded-inline-arrestors?app=tia. Inside the app the user is locked to the
 * TIA tool only and gets the mobile-first view regardless of viewport.
 *
 * Detection, in order:
 *   1. Capacitor native runtime (bridge injected by the shell)
 *   2. ?app=tia query param (persisted — SPA navigation drops the param)
 */
const TIA_APP_FLAG = 'flameco.tia.appShell';

declare global {
  interface Window {
    Capacitor?: { isNativePlatform?: () => boolean };
    /** Print bridge injected by the Android shell (MainActivity.PrintBridge). */
    AndroidPrint?: { print: (jobName: string) => void };
  }
}

/**
 * Print the current page. window.print() is a silent no-op inside the Android
 * WebView, so the shell injects window.AndroidPrint (system print dialog —
 * print or save as PDF, honoring @media print CSS). Prefer it when present.
 */
export function printPage(jobName = 'TIA Report'): void {
  if (window.AndroidPrint?.print) {
    window.AndroidPrint.print(jobName);
    return;
  }
  window.print();
}

export function isTiaApp(): boolean {
  try {
    if (window.Capacitor?.isNativePlatform?.()) return true;
    const params = new URLSearchParams(window.location.search);
    if (params.get('app') === 'tia') {
      localStorage.setItem(TIA_APP_FLAG, '1');
      return true;
    }
    return localStorage.getItem(TIA_APP_FLAG) === '1';
  } catch {
    return false;
  }
}

/** Test/dev helper: leave app mode (e.g. from a debug menu). */
export function clearTiaAppFlag(): void {
  try {
    localStorage.removeItem(TIA_APP_FLAG);
  } catch {
    /* ignore */
  }
}
